import {
  renderDate,
  renderDateTime,
  renderOrderStatus,
  renderTextLineClamp,
  renderPrice,
} from 'helpers';
import { type TFunc, useTranslation } from 'libs/i18n';
import { type TableColumn } from 'libs/ui';

import { type OperationsListItem } from '../../types';

import { renderClient } from './renders/client';
import { renderExecutionPlace } from './renders/executionPlace';
import { renderStatus } from './renders/status';
import { renderTrader } from './renders/trader';

interface GetColumnsArgs {
  brokerTimezoneOffset: number;
}

export const getColumns = (
  t: TFunc,
  { brokerTimezoneOffset }: GetColumnsArgs
): TableColumn<OperationsListItem>[] => [
  {
    field: 'orderNumber',
    title: t('operation.operations.columns.orderNumber'),
    flex: 1,
    minWidth: 144,
    renderCell: renderTextLineClamp,
  },
  {
    field: 'clientName',
    title: t('operation.operations.columns.clientName'),
    flex: 1,
    minWidth: 225,
    renderCell: renderClient,
  },
  {
    field: 'orderOperationType',
    title: t('operation.operations.columns.orderOperationType'),
    flex: 1,
    minWidth: 150,
    renderCell: (item) =>
      renderTextLineClamp(t(`order.operationTypes.${item.orderOperationType}`)),
  },
  {
    field: 'orderExecutionTermsOrderType',
    title: t('operation.operations.columns.orderExecutionTermsOrderType'),
    flex: 1,
    minWidth: 105,
    renderCell: (item) =>
      item.orderExecutionTermsOrderType
        ? renderTextLineClamp(
            t(
              `order.executionTermsOrderType.${item.orderExecutionTermsOrderType}`
            )
          )
        : null,
  },
  {
    field: 'orderEndDate',
    title: t('operation.operations.columns.orderEndDate'),
    flex: 1,
    minWidth: 109,
    sortable: true,
    renderCell: (item, field) => renderDate(item, field, brokerTimezoneOffset),
  },
  {
    field: 'executedRequested',
    title: t('operation.operations.columns.executedRequested'),
    flex: 1,
    minWidth: 94,
    renderCell: renderTextLineClamp,
  },
  {
    field: 'orderStatus',
    title: t('operation.operations.columns.orderStatus'),
    flex: 1,
    minWidth: 296,
    sortable: true,
    renderCell: (item) => renderOrderStatus({ status: item.orderStatus }),
  },
  {
    field: 'number',
    title: t('operation.operations.columns.number'),
    flex: 1,
    minWidth: 120,
    renderCell: renderTextLineClamp,
  },
  {
    field: 'executedAt',
    title: t('operation.operations.columns.executedAt'),
    flex: 1,
    minWidth: 109,
    sortable: true,
    renderCell: (item, field) =>
      renderDateTime(item, field, brokerTimezoneOffset),
  },
  {
    field: 'settlementDate',
    title: t('operation.operations.columns.settlementDate'),
    flex: 1,
    minWidth: 121,
    sortable: true,
    renderCell: (item, field) => renderDate(item, field, brokerTimezoneOffset),
  },
  {
    field: 'isin',
    title: t('operation.operations.columns.isin'),
    flex: 1,
    minWidth: 160,
    renderCell: (item) => renderTextLineClamp(item.isin),
  },
  {
    field: 'currency',
    title: t('operation.operations.columns.currency'),
    flex: 1,
    minWidth: 75,
    renderCell: (item) => renderTextLineClamp(item.currency),
  },
  {
    field: 'netUnitPrice',
    title: t('operation.operations.columns.unitPrice'),
    flex: 1,
    minWidth: 100,
    renderCell: renderPrice,
  },
  {
    field: 'quantity',
    title: t('operation.operations.columns.quantity'),
    flex: 1,
    minWidth: 102,
    renderCell: renderTextLineClamp,
  },
  {
    field: 'volume',
    title: t('operation.operations.columns.volume'),
    flex: 1,
    minWidth: 119,
    renderCell: renderPrice,
  },
  {
    field: 'executionPlace',
    title: t('operation.operations.columns.executionPlace'),
    flex: 1,
    minWidth: 115,
    renderCell: renderExecutionPlace,
  },
  {
    field: 'trader',
    title: t('operation.operations.columns.traderName'),
    flex: 1,
    minWidth: 223,
    renderCell: renderTrader,
  },
  {
    field: 'status',
    title: t('operation.operations.columns.status'),
    flex: 1,
    minWidth: 143,
    sortable: true,
    renderCell: (item) => renderStatus(item),
  },
];

export const useColumns = ({ brokerTimezoneOffset }: GetColumnsArgs) => {
  const { t } = useTranslation();
  const columns = getColumns(t, { brokerTimezoneOffset });

  return columns;
};
